import { createSelector } from '@reduxjs/toolkit';

import { PokerCategory } from '@/services/common/types/pokerCategory';
import {
  AvailableActions,
  HandState,
  PlayerSeatDto,
  SeatDto,
  TableState,
  WinningData,
} from '@/services/table/tableState';
import { ReducerState } from '@/services/common/lib/reducerState';
import { createStakeString, TableStakeInfo } from '@/common/utils/utils';
import { Operation } from '@/services/table/tableOperations';
import { Card, EventSounds } from '@/services/table/tableTypes';

import { Selector } from '../common/types/selector';

export const selectTableState = (tableId: string): Selector<TableState | undefined> =>
  createSelector([(state: ReducerState) => state.tables], (tables: Record<string, TableState>) => tables?.[tableId]);

export const selectTableUrl = (tableId: string): Selector<string | undefined> =>
  createSelector([selectTableState(tableId)], (tableState) => tableState?.url);

export const selectPlayerIsWaiting = (tableId: string): Selector<boolean | undefined> =>
  createSelector([selectTableState(tableId)], (tableState) => tableState?.playerSeat?.waiting);

export const selectTableTitle = (tableId: string): Selector<string | undefined> =>
  createSelector([selectTableState(tableId)], (tableState) => {
    if (tableState?.name && tableState?.game) {
      const tableStakeInfo: TableStakeInfo = {
        ante: tableState.ante,
        smallBet: tableState.smallBet,
        bigBet: tableState.bigBet,
        smallBlind: tableState.smallBlind,
        bigBlind: tableState.bigBlind,
      };
      return `${tableState.name} - ${tableState.game} ${createStakeString(tableStakeInfo)}`;
    }
    return undefined;
  });

export const selectSeats = (tableId: string): Selector<(SeatDto | null)[] | undefined> =>
  createSelector([selectTableState(tableId)], (tableState) => tableState?.seats);

export const selectPlayerSeat = (tableId: string): Selector<PlayerSeatDto | undefined> =>
  createSelector([selectTableState(tableId)], (tableState) => tableState?.playerSeat);

export const selectPlayerIsSitout = (tableId: string): Selector<boolean> =>
  createSelector([selectPlayerSeat(tableId)], (playerSeat) => playerSeat?.active === false);

export const selectPlayersSitting = (tableId: string): Selector<number> =>
  createSelector([selectTableState(tableId)], (tableState) => tableState?.seats?.filter((s) => !!s).length || 0);

export const selectCanWaitForBigBlind = (tableId: string): Selector<boolean> =>
  createSelector(
    [selectTableState(tableId), selectPlayerSeat(tableId), selectPlayerIsSitout(tableId)],
    (tableState, playerSeat, isSitout) => {
      return Boolean(
        !isSitout &&
          tableState?.dealerSeat &&
          tableState.dealerSeat >= 0 && // should not be showing this before the game starts
          tableState?.maxPlayers &&
          tableState.maxPlayers !== 2 && // should not be showing this if its a heads up game
          tableState?.currentVariant?.category !== PokerCategory.STUD &&
          playerSeat?.playerIndex === null &&
          (playerSeat.handsDealt === 0 || playerSeat.skippedSB || playerSeat.skippedBB || playerSeat.newPlayer),
      );
    },
  );
export const selectTableStateUndefined = (tableId: string): Selector<boolean> =>
  createSelector([selectTableState(tableId)], (tableState) => tableState === undefined);

export const selectGame = (tableId: string): Selector<string | undefined> =>
  createSelector([selectTableState(tableId)], (tableState) => tableState?.game);
export const selectName = (tableId: string): Selector<string | undefined> =>
  createSelector([selectTableState(tableId)], (tableState) => tableState?.name);
export const selectAnte = (tableId: string): Selector<number | undefined> =>
  createSelector([selectTableState(tableId)], (tableState) => tableState?.ante);
export const selectSmallBlind = (tableId: string): Selector<number | undefined> =>
  createSelector([selectTableState(tableId)], (tableState) => tableState?.smallBlind);
export const selectBigBlind = (tableId: string): Selector<number | undefined> =>
  createSelector([selectTableState(tableId)], (tableState) => tableState?.bigBlind);
export const selectSmallBet = (tableId: string): Selector<number | undefined> =>
  createSelector([selectTableState(tableId)], (tableState) => tableState?.smallBet);
export const selectBigBet = (tableId: string): Selector<number | undefined> =>
  createSelector([selectTableState(tableId)], (tableState) => tableState?.bigBet);
export const selectMaxPlayers = (tableId: string): Selector<number | undefined> =>
  createSelector([selectTableState(tableId)], (tableState) => tableState?.maxPlayers);
export const selectWaitlist = (tableId: string): Selector<string[] | undefined> =>
  createSelector([selectTableState(tableId)], (tableState) => tableState?.waitlist);
export const selectIsTableFull = (tableId: string): Selector<boolean> =>
  createSelector(
    [selectMaxPlayers(tableId), selectPlayersSitting(tableId)],
    (maxPlayers, playersSitting) => playersSitting > 0 && playersSitting === maxPlayers,
  );
export const selectHandState = (
  tableId: string,
): Selector<
  | {
      handId: string;
      state: HandState;
      availableActions: AvailableActions;
      lastOperation?: Operation;
    }
  | undefined
> => createSelector([selectTableState(tableId)], (tableState) => tableState?.handState);

export const selectTableSounds = (tableId: string): Selector<EventSounds | undefined> =>
  createSelector([selectTableState(tableId)], (tableState) => tableState?.sound);
export const selectIsActingPlayer = (tableId: string): Selector<boolean> =>
  createSelector([selectPlayerSeat(tableId)], (playerSeat) => Boolean(playerSeat?.isActingPlayer));
export const selectWinnersData = (tableId: string): Selector<WinningData | undefined> =>
  createSelector([selectTableState(tableId)], (tableState) => tableState?.winnersData);
export const selectIsWinner = (tableId: string): Selector<boolean> =>
  createSelector([selectPlayerSeat(tableId), selectWinnersData(tableId)], (playerSeat, winnersData) =>
    Boolean(typeof playerSeat?.seat === 'number' && winnersData && winnersData.playerIndexes.includes(playerSeat.seat)),
  );
export const selectWinnersCards = (tableId: string): Selector<Card[] | undefined> =>
  createSelector([selectWinnersData(tableId)], (winnersData) => winnersData?.cards);

export const selectCurrentVariantCategory = (tableId: string): Selector<PokerCategory | undefined> =>
  createSelector([selectTableState(tableId)], (tableState) => tableState?.currentVariant?.category);

export const selectAllHoleDealingStatuses = (tableId: string): Selector<boolean[] | undefined> =>
  createSelector([selectHandState(tableId)], (handState) => handState?.state?.all_hole_dealing_statuses);

export const selectHandStateStatuses = (tableId: string): Selector<boolean[] | undefined> =>
  createSelector([selectHandState(tableId)], (handState) => handState?.state?.statuses);

export const selectHandId = (tableId: string): Selector<string | undefined> =>
  createSelector([selectHandState(tableId)], (handState) => handState?.handId);

export const selectIsRealMoney = (tableId: string): Selector<boolean> =>
  createSelector([selectTableState(tableId)], (tableState) => Boolean(tableState?.isRealMoney));
export const selectMaxBuyin = (tableId: string): Selector<number | undefined> =>
  createSelector([selectTableState(tableId)], (tableState) => tableState?.maxBuyin);
export const selectMinBuyin = (tableId: string): Selector<number | undefined> =>
  createSelector([selectTableState(tableId)], (tableState) => tableState?.minBuyin);
